import format from "date-fns/format"
import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"
import { useQuery } from "react-query"
import { ExportAction } from "src/actions/dry_stack"

import BoatPreviewLink from "src/components/BoatPreviewLink"
import DataTableV2 from "src/components/DataTableV2"
import InlineNotice from "src/components/InlineNotice"
import TrackingLink from "src/components/TrackingLink"

import { bulkExportLaunches, queryLaunchesByBoat } from "src/api/DryStack"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

import LaunchReportDatePickers from "./LaunchReportDatePickers"
import LaunchReportEmptyState from "./LaunchReportEmptyState"
import LaunchReportError from "./LaunchReportError"
import LaunchReportLoading from "./LaunchReportLoading"

const LaunchesByBoatReport = ({ trackingProps }) => {
  const [queryEnabled, setQueryEnabled] = useState(true)
  const [startDate, setStartDate] = useState(new Date())
  const [endDate, setEndDate] = useState(new Date())
  const [selection, setSelection] = useState([])
  const marinaSlug = getCurrentMarinaSlug()
  const showToast = useToast()
  const tracker = useTracker()

  const { data, isLoading, isError } = useQuery(
    [
      "launchesByBoat",
      marinaSlug,
      format(startDate, "yyyy-MM-dd"),
      format(endDate, "yyyy-MM-dd"),
    ],
    () =>
      queryLaunchesByBoat(
        marinaSlug,
        format(startDate, "yyyy-MM-dd"),
        format(endDate, "yyyy-MM-dd")
      ),
    {
      enabled: queryEnabled,
      refetchOnWindowFocus: false,
      retry: false,
    }
  )

  const sortedLaunchData = useMemo(
    () =>
      data?.launchesByBoat?.sort(
        (a, b) =>
          b.scheduleAppointmentsWithinRange - a.scheduleAppointmentsWithinRange
      ) || [],
    [data]
  )

  const columns = [
    {
      key: "contactBoat",
      header: "Boat",
      render: (row) => {
        const boat = row.contactBoat
        if (!boat) return row.contactBoat?.name || "-"

        return <BoatPreviewLink boat={boat} />
      },
    },
    {
      key: "contact",
      header: "Customer",
      render: (row) => row.contact.name,
    },
    {
      key: "encodedId",
      header: "Reservation",
      render: (row) => (
        <TrackingLink
          className="text-link font-semibold text-blue-600"
          href={row.managePath}
          eventName="dry_stack_schedule_view:launch_reports_reservation_link_clicked"
          eventProperties={trackingProps}
          newTab
        >
          #{row.encodedId}
        </TrackingLink>
      ),
    },
    {
      key: "scheduleAppointmentsWithinRange",
      header: "Launch Count",
    },
  ]

  const handleBulkExport = async (selectedIds) => {
    if (!selectedIds?.length) return

    tracker.trackEvent("dry_stack_schedule_view:bulk_export_requested", {
      ...trackingProps,
      report_name: "launches_by_boat",
      launch_count: selectedIds.length,
    })

    try {
      await bulkExportLaunches({
        marinaSlug,
        startDate: format(startDate, "yyyy-MM-dd"),
        endDate: format(endDate, "yyyy-MM-dd"),
        ids: selectedIds,
      })
      tracker.trackEvent("dry_stack_schedule_view:bulk_export_completed", {
        ...trackingProps,
        report_name: "launches_by_boat",
        launch_count: selectedIds.length,
      })
      setSelection([])
      showToast("Export is being generated and will be emailed shortly.", {
        type: "success",
      })
    } catch (_error) {
      tracker.trackEvent("dry_stack_schedule_view:bulk_export_failed", {
        ...trackingProps,
        report_name: "launches_by_boat",
        launch_count: selectedIds.length,
      })
      showToast("Unable to start export. Please try again.", {
        type: "error",
      })
    }
  }

  const tableActions = [ExportAction({ onExport: handleBulkExport })]

  const renderData = () => {
    if (isLoading) {
      return <LaunchReportLoading />
    } else if (isError) {
      return <LaunchReportError />
    } else if (!data?.launchesByBoat?.length) {
      return (
        <LaunchReportEmptyState description="Select dates with launches to view number of launches per boat" />
      )
    }

    return (
      <DataTableV2
        name="launchesByBoat"
        columns={columns}
        data={sortedLaunchData}
        idKey="encodedId"
        actions={tableActions}
        selection={selection}
        onSelectionChange={setSelection}
        hideSettings
        onPrint={() => Promise.resolve(sortedLaunchData)}
      />
    )
  }

  return (
    <div className="px-4">
      <LaunchReportDatePickers
        endDate={endDate}
        reportName={"launches_by_boat"}
        setEndDate={setEndDate}
        setStartDate={setStartDate}
        setQueryEnabled={setQueryEnabled}
        startDate={startDate}
        trackingProps={trackingProps}
      />
      <div className="mb-3">
        <InlineNotice
          variant="info"
          icon="fa-file-export"
          iconStyle="solid"
          dismissible
          storagePrefix="launches_by_boat"
          storageKey="export_moved_to_bulk_actions_v1"
        >
          Looking for Export? Select rows, then click <strong>Export</strong>.
        </InlineNotice>
      </div>

      {renderData()}
    </div>
  )
}

LaunchesByBoatReport.propTypes = {
  trackingProps: PropTypes.shape({
    marina_id: PropTypes.string.isRequired,
    marina_name: PropTypes.string.isRequired,
  }),
}

export default LaunchesByBoatReport
